import React, { FunctionComponent, useMemo, useState } from "react";
import { Modal, OverlayTrigger, Popover, Table, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { ApplicationState } from "../../../../store";
import { hideFileUploadErrorModal } from "../../../../store/onboarding/actions";
import { faExclamationCircle, faExclamationTriangle, faUserCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
const { Tabs, Tab } = require("react-bootstrap");


type OwnProps = {};

type Props = OwnProps;


const FileUploadErrorModal: FunctionComponent<Props> = (props) => {
	const dispatch = useDispatch();

	const show = useSelector<ApplicationState, boolean | undefined>(
		(s) => s.onboarding.modalsState.fileUploadErrorModal
	);

	const messages = useSelector<ApplicationState, any>(
		(s) => s.onboarding.fileUploadErrorMessage
	);

	const errorMessage = useMemo(() => {
		const tableRows = (messageObject: any) => {
			return messageObject.table_data?.rows.map((row: any) => {
				return {
					fields: row.fields,
					highlights: row.highlights,
					is_new_student: row.is_new_student
				};
			});
		};

		const createMsgElement = (instruction: string, i: number) => {
			const isHeader =
				i === 0 && instruction.toLowerCase().includes("valid values:");

			return (
				<li
				key={i}
				style={
					isHeader
					? {
						listStyleType: "none",
						marginLeft: 0,
						paddingLeft: 0,
						paddingBottom: "10px"
						}
					: {
						marginLeft: "1.5rem", // indent other bullets manually
						}
				}
				>
				{instruction}
				</li>
			);
		}

		const messageObjectBody = (messageObject: any, tableRows: any[]) => {
			return (
				<>
					{messageObject.miscellaneous_errors.length 
						?
							<div className="alert alert-warning mb-4">
								<strong>
									<FontAwesomeIcon icon={faExclamationTriangle} className="text-danger" /> Miscellaneous Issues:
								</strong>
								<ul className="mb-0 mt-2">
									{messageObject.miscellaneous_errors.map((error: string, idx: number) => (
										<li key={idx}>{error}</li>
									))}
								</ul>
							</div>
						: null
					}
					{tableRows.length
					?
						<>
							{/* Field Instructions */}
							{messageObject.metadata.headers.length ? (
								<>
									{messageObject.metadata.headers.map((header: string, idx: number) => (
										<p key={idx}>
											<span className="font-weight-bold">{header}</span>
											<ul style={{ paddingLeft: 0, marginLeft: 0 }}>
												{messageObject.metadata.instructions[header].map(
													(instruction: string, i: number) => createMsgElement(instruction, i)
												)}
												</ul>
										</p>
									))}
								</>
							) : null}
							{messageObject.metadata.headers.length ? (
								<>
									<hr />
									{messageObject.table_data.headers.length ? <h3 className="text-center font-weight-bold my-3">See the Highlighted Cells</h3> : null}
									<div
										style={{
											maxHeight: 300,
											overflowX: "auto", 
											overflowY: "auto",
											marginTop: "10px",
											minWidth: 0,
											scrollbarWidth: "thin"
										}}
										className="sticky-table-header"
									>
										<Table
											bordered
											className="error-highlight-table goalTable"
											style={{ minWidth: messageObject.table_data.headers.length > 4 ? "1500px" : "" }}
										>
											<thead>
												<tr>
												{messageObject.table_data.headers.map((h: string) => (
													<th key={h}>{h}</th>
												))}
												</tr>
											</thead>
											<tbody>
												{tableRows.map((row: any, rowIndex: number) => (
												<tr key={rowIndex}>
													{messageObject.table_data.headers.map((header: string) => {
													const hasError = row.highlights[header];
													const isNewStudent = row.is_new_student[header] == 'True';
													return (
														<td
														key={header}
														className={`cell ${hasError ? "error-cell" : "no-error-cell"}`}
														style={{ height: "49.22px" }}
														>
														<span className="cell-value">
															{row.fields[header] || ""}
														</span>
														{isNewStudent && (
															<OverlayTrigger
															trigger={["hover", "focus"]}
															placement="top"
															overlay={
																<Popover className="p-2" id="tooltip-top">
																New Student
																</Popover>
															}
															>
															<span className={`error-icon ${ hasError ? "mr-4" : ""}`}>
																<FontAwesomeIcon
																icon={faUserCheck}
																size="lg"
																className="purpleText"
																/>
															</span>
															</OverlayTrigger>
														)}
														{hasError && (
															<OverlayTrigger
															trigger={["hover", "focus"]}
															placement="top"
															overlay={
																<Popover className="p-2" id="tooltip-top">
																{row.highlights[header].map((msg: string, i: number) => {
																	if (msg.includes("##")) {
																	const tdHoverMsg = msg.split("##").map((s: string) => s.trim());
																	return (
																		<ul key={i} style={{ paddingLeft: 0, marginLeft: 0 }}>
																		{tdHoverMsg.map((hoverMsg: string, i: number) => createMsgElement(hoverMsg, i))}
																		</ul>
																	);
																	} else {
																	return <p key={i} className="mb-0">{msg}</p>
																	}
																})}
																</Popover>
															}
															>
															<span className="error-icon">
																<FontAwesomeIcon
																icon={faExclamationCircle}
																size="lg"
																className="purpleText"
																/>
															</span>
															</OverlayTrigger>
														)}
														</td>
													);
													})}
												</tr>
												))}
											</tbody>
										</Table>
									</div>
								</>
							) : null}
						</>
					: null}
				</>
			);
		};

		// If multiple files, use Tabs. If only one, just show content.
		if(messages) {
			if (Array.isArray(messages) ? messages.length && messages[0].tab_name : messages.tab_name) {
				// Use react-bootstrap Tabs
				return (
					<Tabs defaultActiveKey={messages[0].tab_name} id="file-upload-error-tabs" className="mb-3">
						{messages.map((messageObject: any, idx: number) => (
							<Tab eventKey={messageObject.tab_name} title={messageObject.tab_name} key={idx}>
								{messageObjectBody(messageObject, tableRows(messageObject))}
							</Tab>
						))}
					</Tabs>
				);
			} else {
				// Only one file, no tabs
				if(Array.isArray(messages) && messages.length === 1) {
					return messageObjectBody(messages[0], tableRows(messages[0]));
				} else {
					return messageObjectBody(messages, tableRows(messages));
				}
			}
		}
	}, [messages]);

	const handleHide = () => {
		dispatch(hideFileUploadErrorModal());
	};

	return (
		<Modal
			show={show}
			onHide={() => handleHide()}
			animation={false}
			backdropClassName="customDarkModalBackdrop inner in"
			centered
			size="xl"
			backdrop="static"
			keyboard={false}
		>
			<Modal.Header closeButton className="whiteModalHeader" style={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
				<div style={{ flex: 1, textAlign: "center" }}>
					<span className="alert alert-danger p-2 m-0 d-inline-block">
						Failed! There is an issue(s) with the upload.
					</span>
				</div>
			</Modal.Header>
			<Modal.Body>
				{errorMessage}
				<hr />
				<div className="btnActions d-flex justify-content-end">
					<button className="blueBtnSm" onClick={() => handleHide()}>
						Close
					</button>
				</div>
			</Modal.Body>
		</Modal>
	);
};

export default FileUploadErrorModal;
